import { useTranslations } from '@vocab/react';
import {
  Box,
  Column,
  Columns,
  Heading,
  Stack,
  Text,
} from 'braid-design-system';

import appTranslations from 'src/.vocab';
import DancingDots from 'src/App/Components/DancingDots';
import TrackingButtonLink from 'src/App/Components/TrackingButtonLink/TrackingButtonLink';
import TrackingTextLink from 'src/App/Components/TrackingTextLink/TrackingTextLink';
import { analyticsEvents } from 'src/constants/analytics';
import {
  TRACKING_DASHBOARD_JOB_LIST,
  TRACKING_RECENT_JOBS,
} from 'src/constants/tracking';
import { useAccountContext, useDashboardJobs } from 'src/modules/ApiClient';
import { getCreateNewJobUrl, getJobsListUrl } from 'src/utils/getUrls';
import { isActiveOrExpiredJob, isDraftJobItem } from 'src/utils/job';

import translations from './.vocab';
import JobsList from './JobsList';

const RecentJobs = () => {
  const { data: accountContext } = useAccountContext();

  const { loading, error, data: jobsInfo } = useDashboardJobs();
  const { t } = useTranslations(translations);
  const { t: appTrans } = useTranslations(appTranslations);

  if (error) {
    return (
      <Heading level="3">
        {appTrans('Error')}: {error.message}
      </Heading>
    );
  }

  if (
    loading ||
    !accountContext ||
    jobsInfo?.dashboardJobs.__typename !== 'DashboardJobsResponseSuccess' ||
    jobsInfo?.dashboardJobsCounts.__typename !==
      'DashboardJobsCountsResponseSuccess'
  ) {
    return <DancingDots />;
  }

  const jobs = jobsInfo.dashboardJobs.jobs.edges.map((edge) => edge.node);

  if (jobs.length === 0) {
    return null;
  }

  const canCreateJobs =
    accountContext.currentAdvertiser.permissionOptions.canCreateJobs;

  const hasCopyableJobs = jobs.some(
    (job) => !isDraftJobItem(job) && isActiveOrExpiredJob(job),
  );

  const hasUpgradeableJobs = jobs.some(
    (job) => !isDraftJobItem(job) && job.canBeUpgraded,
  );

  const renderHeadingContent = () => {
    if (canCreateJobs && hasUpgradeableJobs) {
      return t('Upgrade to attract applicants faster');
    }
    if (canCreateJobs && hasCopyableJobs) {
      return t('Save time by copying your previous ads');
    }
    return t('My recent job ads');
  };

  const {
    directExpired,
    directPosted,
    indirectExpired,
    indirectPosted,
    draftsCount,
  } = jobsInfo.dashboardJobsCounts;

  const numberOfJobAds =
    (draftsCount ?? 0) +
    (directExpired ?? 0) +
    (directPosted ?? 0) +
    (indirectExpired ?? 0) +
    (indirectPosted ?? 0);

  return (
    <Stack space="large">
      <Columns space="medium" collapseBelow="tablet" alignY="center">
        <Column>
          <Heading level="3">{renderHeadingContent()}</Heading>
        </Column>
        <Column width="content">
          {canCreateJobs && (
            <TrackingButtonLink
              href={getCreateNewJobUrl()}
              variant="solid"
              tone="brandAccent"
              trackingEventName={analyticsEvents.TRACK_CREATE_JOB}
              trackingActionOrigin={TRACKING_DASHBOARD_JOB_LIST}
            >
              {t('Create a job ad button')}
            </TrackingButtonLink>
          )}
        </Column>
      </Columns>

      <JobsList jobs={jobs} />
      {numberOfJobAds > 5 && (
        <Box textAlign={['center', 'right']}>
          <Text>
            <TrackingTextLink
              href={getJobsListUrl()}
              trackingEventName={analyticsEvents.TRACK_VIEW_ALL_JOBS}
              trackingActionOrigin={TRACKING_RECENT_JOBS}
              trackingData={{ entryPoint: 'dashboard job list' }}
            >
              {t('View all job ads button')}
            </TrackingTextLink>
          </Text>
        </Box>
      )}
    </Stack>
  );
};

export default RecentJobs;
